import { Component } from "react";

/**
 * Top-level error boundary. Catches any render-time exception in the tree
 * below it and renders a graceful fallback instead of blanking the page.
 *
 * This is the safety net for the mobile-blank-page class of bugs where a
 * single VideoCard or Feed effect crashes → whole tree unmounts → user
 * sees a white screen with no way out.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
  }

  handleReload = () => {
    // Reset local state and reload the current route
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null });
    if (typeof window !== "undefined") {
      window.location.assign("/");
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        data-testid="error-boundary-fallback"
        className="min-h-screen flex flex-col items-center justify-center bg-black text-white px-6 text-center"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-3">
          // signal.lost
        </p>
        <h1 className="font-display font-black text-3xl mb-3">
          something glitched
        </h1>
        <p className="text-white/60 text-sm max-w-sm mb-8">
          The feed hit an unexpected error. Nothing was lost — reload the page
          to get back in.
        </p>
        <div className="flex gap-3">
          <button
            data-testid="error-boundary-reload"
            onClick={this.handleReload}
            className="bg-[#39FF14] text-black font-bold uppercase tracking-wider px-5 py-3 hover:shadow-[0_0_18px_rgba(57,255,20,0.5)] transition-shadow"
          >
            Reload
          </button>
          <button
            data-testid="error-boundary-home"
            onClick={this.handleHome}
            className="border border-white/20 hover:border-white/60 font-bold uppercase tracking-wider px-5 py-3"
          >
            Go home
          </button>
        </div>
        {this.state.error?.message && (
          <p className="mt-6 font-mono text-[10px] text-white/30 max-w-sm break-words">
            {String(this.state.error.message).slice(0, 240)}
          </p>
        )}
      </div>
    );
  }
}
